import { createContext, useContext, useState, useCallback, useRef } from 'react'

// Apple MapKit JWTs are origin-locked. Apple's wildcard `*.voyabay.com` matches
// subdomains (www, app, staging…) but NOT the apex `voyabay.com`. So we ship two
// tokens and pick the right one at runtime by hostname.
const pickMapKitToken = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const apex = import.meta.env.VITE_APPLE_MAPKIT_TOKEN_APEX || ''
  const wildcard = import.meta.env.VITE_APPLE_MAPKIT_TOKEN || ''
  if (hostname === 'voyabay.com' && apex) return apex
  return wildcard || apex
}
const MAPKIT_TOKEN = pickMapKitToken()
const MAPKIT_SCRIPT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js'
const MAPKIT_LIBRARIES = ['map', 'annotations']

const MapsContext = createContext({
  isLoaded: false,
  loadError: null,
  loadMaps: () => {}
})

const toCoordinate = (value) => {
  if (!value) return null
  if (value instanceof window.mapkit.Coordinate) return value
  if (typeof value.lat === 'function' && typeof value.lng === 'function') {
    return new window.mapkit.Coordinate(value.lat(), value.lng())
  }
  return new window.mapkit.Coordinate(Number(value.lat ?? value.latitude), Number(value.lng ?? value.longitude))
}

const spanForZoom = (zoom, latitude = 0) => {
  const safeZoom = Number.isFinite(zoom) ? Math.max(1, Math.min(19, zoom)) : 13
  const latitudeScale = Math.max(0.25, Math.cos(Math.abs(latitude) * Math.PI / 180))
  const latitudeDelta = 360 / Math.pow(2, safeZoom)
  return new window.mapkit.CoordinateSpan(latitudeDelta, latitudeDelta / latitudeScale)
}

const zoomForRegion = (region) => {
  const latitudeDelta = Math.max(0.00001, Number(region?.span?.latitudeDelta) || 0.05)
  return Math.max(1, Math.min(19, Math.round(Math.log2(360 / latitudeDelta))))
}

const distanceBetween = (a, b) => {
  const lat1 = Number(typeof a.lat === 'function' ? a.lat() : a.latitude ?? a.lat)
  const lon1 = Number(typeof a.lng === 'function' ? a.lng() : a.longitude ?? a.lng)
  const lat2 = Number(typeof b.lat === 'function' ? b.lat() : b.latitude ?? b.lat)
  const lon2 = Number(typeof b.lng === 'function' ? b.lng() : b.longitude ?? b.lng)
  const toRad = (value) => value * Math.PI / 180
  const earthRadiusMeters = 6371008.8
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const rLat1 = toRad(lat1)
  const rLat2 = toRad(lat2)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rLat1) * Math.cos(rLat2) * Math.sin(dLon / 2) ** 2
  return 2 * earthRadiusMeters * Math.asin(Math.min(1, Math.sqrt(h)))
}

const createTransparentPixelUrl = () => (
  'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>')
)

function installVoyabayMapKitAdapter() {
  if (!window.mapkit || window.voyabayMaps?.__provider === 'mapkit') return

  class LatLng {
    constructor(lat, lng) {
      this._lat = Number(lat)
      this._lng = Number(lng)
    }
    lat() { return this._lat }
    lng() { return this._lng }
  }

  class LatLngBounds {
    constructor() {
      this._north = -Infinity
      this._east = -Infinity
      this._south = Infinity
      this._west = Infinity
    }
    extend(value) {
      const lat = Number(typeof value.lat === 'function' ? value.lat() : value.lat ?? value.latitude)
      const lng = Number(typeof value.lng === 'function' ? value.lng() : value.lng ?? value.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return this
      this._north = Math.max(this._north, lat)
      this._east = Math.max(this._east, lng)
      this._south = Math.min(this._south, lat)
      this._west = Math.min(this._west, lng)
      return this
    }
    contains(value) {
      if (!this.isValid()) return false
      const lat = Number(typeof value.lat === 'function' ? value.lat() : value.lat ?? value.latitude)
      const lng = Number(typeof value.lng === 'function' ? value.lng() : value.lng ?? value.longitude)
      return lat <= this._north && lat >= this._south && lng <= this._east && lng >= this._west
    }
    getNorthEast() { return new LatLng(this._north, this._east) }
    getSouthWest() { return new LatLng(this._south, this._west) }
    isValid() {
      return [this._north, this._east, this._south, this._west].every(Number.isFinite)
    }
    toRegion(paddingFactor = 0.18) {
      if (!this.isValid()) return null
      const center = new window.mapkit.Coordinate(
        (this._north + this._south) / 2,
        (this._east + this._west) / 2
      )
      const latDelta = Math.max(0.004, (this._north - this._south) * (1 + paddingFactor))
      const lngDelta = Math.max(0.004, (this._east - this._west) * (1 + paddingFactor))
      return new window.mapkit.CoordinateRegion(center, new window.mapkit.CoordinateSpan(latDelta, lngDelta))
    }
  }

  class Size {
    constructor(width, height) {
      this.width = Number(width)
      this.height = Number(height)
    }
  }

  class Point {
    constructor(x, y) {
      this.x = Number(x)
      this.y = Number(y)
    }
  }

  const makeListener = (target, eventName, handler) => {
    target.addEventListener(eventName, handler)
    return { remove: () => target.removeEventListener(eventName, handler), target, eventName, handler }
  }

  class Map {
    constructor(element, options = {}) {
      const center = toCoordinate(options.center) || new window.mapkit.Coordinate(39.5, -98.35)
      const zoom = Number(options.zoom) || 13
      const span = spanForZoom(zoom, center.latitude)
      const mapType = options.mapType && window.mapkit.Map?.MapTypes
        ? window.mapkit.Map.MapTypes[options.mapType] || options.mapType
        : undefined
      element.style.position = element.style.position || 'relative'
      this._mapkit = new window.mapkit.Map(element, {
        center,
        region: new window.mapkit.CoordinateRegion(center, span),
        ...(mapType ? { mapType } : {}),
        showsZoomControl: options.zoomControl !== false,
        showsMapTypeControl: !!options.mapTypeControl,
        showsCompass: window.mapkit.FeatureVisibility?.Hidden,
        showsScale: window.mapkit.FeatureVisibility?.Hidden,
        showsPointsOfInterest: options.clickableIcons !== false,
        isRotationEnabled: false,
        tintColor: '#009e60'
      })
      this._zoom = zoom
      this._listeners = new Set()
      this._markers = new Set()
    }
    addListener(eventName, callback) {
      const eventMap = {
        click: 'single-tap',
        dragstart: 'scroll-start',
        dragend: 'scroll-end',
        idle: 'region-change-end',
        zoom_changed: 'zoom-end'
      }
      const mapkitEvent = eventMap[eventName] || eventName
      const listener = makeListener(this._mapkit, mapkitEvent, callback)
      this._listeners.add(listener)
      if (eventName === 'zoom_changed') {
        const startListener = makeListener(this._mapkit, 'zoom-start', callback)
        this._listeners.add(startListener)
        const regionListener = makeListener(this._mapkit, 'region-change-end', callback)
        this._listeners.add(regionListener)
        const originalRemove = listener.remove
        listener.remove = () => {
          originalRemove()
          startListener.remove()
          regionListener.remove()
        }
      }
      return listener
    }
    getCenter() {
      const center = this._mapkit.center
      return center ? new LatLng(center.latitude, center.longitude) : null
    }
    setCenter(value) {
      const coordinate = toCoordinate(value)
      if (coordinate) this._mapkit.setCenterAnimated(coordinate, false)
    }
    panTo(value) {
      const coordinate = toCoordinate(value)
      if (coordinate) this._mapkit.setCenterAnimated(coordinate, true)
    }
    getZoom() {
      this._zoom = zoomForRegion(this._mapkit.region)
      return this._zoom
    }
    setZoom(value) {
      const zoom = Math.max(1, Math.min(19, Number(value) || this._zoom || 13))
      this._zoom = zoom
      const center = this._mapkit.center
      this._mapkit.setRegionAnimated(
        new window.mapkit.CoordinateRegion(center, spanForZoom(zoom, center.latitude)),
        false
      )
    }
    getBounds() {
      const region = this._mapkit.region
      if (!region) return null
      const bounding = region.toBoundingRegion()
      const bounds = new LatLngBounds()
      bounds.extend({ lat: bounding.northLatitude, lng: bounding.eastLongitude })
      bounds.extend({ lat: bounding.southLatitude, lng: bounding.westLongitude })
      return bounds
    }
    fitBounds(bounds, options) {
      const paddingPx = typeof options?.padding === 'number' ? options.padding : 80
      const paddingFactor = Math.max(0.05, Math.min(0.5, paddingPx / 400))
      const region = bounds?.toRegion?.(paddingFactor)
      if (region) {
        this._mapkit.setRegionAnimated(region, true)
        this._zoom = zoomForRegion(region)
      }
    }
  }

  class Marker {
    constructor(options = {}) {
      this.position = options.position
      this.icon = options.icon || null
      this.hotelData = null
      this._map = null
      this._annotation = null
      this._listeners = {}
      this._zIndex = options.zIndex || 1
      this._element = this._createElement()
      if (options.map) this.setMap(options.map)
    }
    _createElement() {
      const element = document.createElement('div')
      element.setAttribute('role', 'button')
      this._applyStyle(element)
      element.addEventListener('click', (event) => {
        event.stopPropagation()
        this._emit('click', event)
      })
      element.addEventListener('mouseover', (event) => this._emit('mouseover', event))
      element.addEventListener('mouseout', (event) => this._emit('mouseout', event))
      return element
    }
    _applyStyle(element) {
      const label = this.icon?.label
      if (this.icon?.isCluster) {
        const size = this.icon?.scaledSize?.width || 42
        const bg = this.icon?.clusterSelected ? '#222' : '#1a73e8'
        element.textContent = label || ''
        element.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:${bg};color:#fff;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:${(label || '').length > 2 ? 11 : 13}px;font-weight:700;cursor:pointer;box-shadow:0 1.5px 4px rgba(0,0,0,0.28);`
        return
      }
      if (label) {
        const state = this.icon?.pillState || 'default'
        const isSelected = state === 'selected'
        const isHover = state === 'hover'
        const bg = isSelected ? '#222' : (isHover ? '#009e60' : '#fff')
        const color = (isSelected || isHover) ? '#fff' : '#222'
        const border = isSelected ? '1px solid rgba(0,0,0,0.22)' : (isHover ? '1px solid #008754' : '1px solid rgba(0,0,0,0.2)')
        element.textContent = label
        element.style.cssText = `background:${bg};color:${color};border:${border};padding:3px 10px;border-radius:12px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;font-weight:700;white-space:nowrap;cursor:pointer;box-shadow:0 1px 3px rgba(0,0,0,0.14);line-height:18px;`
        return
      }
      // Icon-URL fallback — caller passed a raw SVG/PNG URL with sizing.
      // Used by the property-pin Markers on the details preview + expanded map.
      if (this.icon?.url) {
        const w = this.icon?.scaledSize?.width || this.icon?.size?.width || 32
        const h = this.icon?.scaledSize?.height || this.icon?.size?.height || 32
        element.textContent = ''
        element.style.cssText = `width:${w}px;height:${h}px;background:transparent url("${this.icon.url}") center/contain no-repeat;cursor:pointer;`
        return
      }
      element.textContent = ''
      element.style.cssText = 'width:32px;height:32px;cursor:pointer;'
    }
    _emit(eventName, event) {
      ;(this._listeners[eventName] || []).forEach((handler) => handler(event))
    }
    addListener(eventName, handler) {
      if (!this._listeners[eventName]) this._listeners[eventName] = []
      this._listeners[eventName].push(handler)
      return {
        remove: () => {
          this._listeners[eventName] = (this._listeners[eventName] || []).filter((item) => item !== handler)
        }
      }
    }
    setIcon(icon) {
      this.icon = icon
      this._applyStyle(this._element)
    }
    setZIndex(zIndex) {
      this._zIndex = zIndex
    }
    setMap(map) {
      if (this._map && this._annotation) {
        try { this._map._mapkit.removeAnnotation(this._annotation) } catch (e) {}
        this._map._markers.delete(this)
      }
      this._annotation = null
      this._map = map || null
      if (map) {
        const coord = toCoordinate(this.position)
        if (coord) {
          this._annotation = new window.mapkit.Annotation(coord, () => this._element, {
            calloutEnabled: false,
            displayPriority: 1000,
            animates: false
          })
          map._mapkit.addAnnotation(this._annotation)
        }
        map._markers.add(this)
      }
    }
  }

  window.voyabayMaps = {
    __provider: 'mapkit',
    Map,
    Marker,
    LatLng,
    LatLngBounds,
    Size,
    Point,
    geometry: {
      spherical: {
        computeDistanceBetween: distanceBetween
      }
    },
    event: {
      trigger: () => {},
      removeListener: (listener) => listener?.remove?.()
    }
  }
}

function loadMapKitScript() {
  return new Promise((resolve, reject) => {
    if (window.mapkit) {
      resolve()
      return
    }

    const existing = document.querySelector(`script[src="${MAPKIT_SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', resolve, { once: true })
      existing.addEventListener('error', reject, { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = MAPKIT_SCRIPT_SRC
    script.async = true
    script.crossOrigin = 'anonymous'
    script.onload = resolve
    script.onerror = () => reject(new Error('Failed to load Apple MapKit JS'))
    document.head.appendChild(script)
  })
}

function hasLoadedMapKitLibraries() {
  const loaded = window.mapkit?.loadedLibraries
  if (!loaded) return false
  const loadedList = Array.isArray(loaded) ? loaded : String(loaded).split(',')
  return MAPKIT_LIBRARIES.every((library) => loadedList.includes(library))
}

function waitForMapKitLibraries() {
  if (hasLoadedMapKitLibraries()) return Promise.resolve()

  return new Promise((resolve, reject) => {
    let settled = false
    const timeoutId = window.setTimeout(() => {
      if (settled) return
      settled = true
      window.mapkit?.removeEventListener?.('load', handleLoad)
      window.mapkit?.removeEventListener?.('load-error', handleLoadError)
      reject(new Error('Timed out loading Apple MapKit JS libraries'))
    }, 12000)

    const finish = () => {
      if (settled || !hasLoadedMapKitLibraries()) return
      settled = true
      window.clearTimeout(timeoutId)
      window.mapkit.removeEventListener('load', handleLoad)
      window.mapkit.removeEventListener('load-error', handleLoadError)
      resolve()
    }

    const handleLoad = () => finish()
    const handleLoadError = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      window.mapkit.removeEventListener('load', handleLoad)
      window.mapkit.removeEventListener('load-error', handleLoadError)
      reject(new Error('Failed to load Apple MapKit JS libraries'))
    }

    window.mapkit.addEventListener('load', handleLoad)
    window.mapkit.addEventListener('load-error', handleLoadError)

    if (typeof window.mapkit.load === 'function') {
      window.mapkit.load(MAPKIT_LIBRARIES)
    }

    finish()
  })
}

function loadAppleMaps() {
  return loadMapKitScript().then(() => {
    if (!MAPKIT_TOKEN) {
      throw new Error('Missing VITE_APPLE_MAPKIT_TOKEN')
    }

    if (!window.mapkit?.loadedLibraries) {
      throw new Error('Apple MapKit JS is unavailable')
    }

    if (!window.mapkit.__voyabayInitialized) {
      window.mapkit.init({
        authorizationCallback: (done) => done(MAPKIT_TOKEN),
        libraries: MAPKIT_LIBRARIES
      })
      window.mapkit.__voyabayInitialized = true
    }

    return waitForMapKitLibraries().then(() => {
      installVoyabayMapKitAdapter()
    })
  })
}

export function AppleMapsProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(!!window.voyabayMaps && window.voyabayMaps.__provider === 'mapkit')
  const [loadError, setLoadError] = useState(null)
  const loadingRef = useRef(false)

  const loadMaps = useCallback(() => {
    if (isLoaded || loadingRef.current) return
    loadingRef.current = true
    loadAppleMaps()
      .then(() => {
        setLoadError(null)
        setIsLoaded(true)
      })
      .catch((err) => setLoadError(err))
      .finally(() => { loadingRef.current = false })
  }, [isLoaded])

  return (
    <MapsContext.Provider value={{ isLoaded, loadError, loadMaps }}>
      {children}
    </MapsContext.Provider>
  )
}

export function useAppleMaps() {
  return useContext(MapsContext)
}

export default MapsContext
